import React, { useState } from 'react';
import { Modal, Card, Table, Dropdown, Menu, Flex, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { ModalFooter } from '~/components/Modal';
import { formatPrice } from '~/Utils/format';
import { formatDateTime } from '~/Utils';
import Can from '~/components/Can';
import Button from '~/components/Button';
import { FaPencilAlt } from 'react-icons/fa';
import { RedTimes, TableView, TitleTag } from '~/stylizations/styles';
import { FiDownload } from 'react-icons/fi';
import { createEvents } from '~/services/hooks/events';
import FormEvents from './forms/form';
import errorHandler from '~/Utils/errorHandler';
import { createConsigneds } from '~/services/hooks/consigneds';

export default function EventFullView({ visible, onClose, event, isMobile }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formVisible, setFormVisible] = useState(false);

  const label = (text) => <span style={{ fontSize: '18px', fontWeight: 'bold' }}>{t(text)}</span>;
  const child = (text) => {
    if (text == null || text == '') {
      return <RedTimes />;
    }

    return <span style={{ fontSize: '15px', fontWeight: 'bold' }}>{t(text)}</span>;
  };
  const title = (text) => <span style={{ fontSize: '24px', fontWeight: 'bold' }}>{t(text)}</span>;
  const renderHTML = (html) => {
    if (html == null || html == '') {
      return <RedTimes />;
    }

    return <div style={{ fontSize: '15px', fontWeight: 'bold' }} dangerouslySetInnerHTML={{ __html: html ?? '' }} />;
  };

  const handleModal = () => {
    setFormVisible(true);
  };

  const allSectorTotals = event?.sectors?.reduce(
    (acc, sector) => {
      sector?.sector_operationals?.forEach((so) => {
        acc.priceTotal += parseFloat(so.price_total) || 0;
        acc.amountTotal += parseInt(so.amount) || 0;
      });
      return acc;
    },
    { priceTotal: 0, amountTotal: 0 }
  );

  const fetchQuote = async (type) => {
    setLoading(true);
    try {
      const url = await createEvents('', `print-quote-internal/${event.id}/${type}`);
      window.open(url, '_blank');
    } catch (error) {
      console.error(error);
      errorHandler(error);
    }
    setLoading(false);
  };

  const fetchPrintTeam = async (type) => {
    setLoading(true);
    try {
      const url = await createEvents('', `print-team/${event.id}/${type}`);
      window.open(url, '_blank');
    } catch (error) {
      console.error(error);
      errorHandler(error);
    }
    setLoading(false);
  };

  const fetchPrintConsigneds = async () => {
    setLoading(true);
    try {
      const url = await createConsigneds('', `export-pdf/${event.id}`);
      window.open(url, '_blank');
    } catch (error) {
      console.error(error);
      errorHandler(error);
    }
    setLoading(false);
  };

  const groupedTeams =
    event?.team?.reduce((acc, item) => {
      if (!acc[item.sector_id]) {
        acc[item.sector_id] = {
          sector_name: item.sector_name,
          records: [],
        };
      }
      acc[item.sector_id].records.push(item);
      return acc;
    }, {}) || {};

  return (
    <Modal
      title={`${t('Informações do Evento')} ${event?.id} - ${event?.name ?? ''}`}
      open={visible}
      onCancel={onClose}
      centered
      width={isMobile ? '100%' : '70%'}
      footer={<ModalFooter showOk={false} onCancel={onClose} />}
    >
      <Spin spinning={loading}>
        <Flex align="center" justify="end" gap={20}>
          <Dropdown
            overlay={
              <Menu>
                <Menu.Item onClick={() => fetchQuote('internal')} key="internal">
                  Interna
                </Menu.Item>
                <Menu.Item onClick={() => fetchQuote('client')} key="client">
                  Cliente
                </Menu.Item>
              </Menu>
            }
            trigger={['click']}
          >
            <Button size="large">
              {t('Emitir via')} <FiDownload />
            </Button>
          </Dropdown>
          <Dropdown
            overlay={
              <Menu>
                <Menu.Item onClick={() => fetchPrintTeam('alphabetic')} key="alphabetic">
                  Ordem alfabética
                </Menu.Item>
                <Menu.Item onClick={() => fetchPrintTeam('excel')} key="excel">
                  Ordem alfabética (.xlsx)
                </Menu.Item>
                <Menu.Item onClick={() => fetchPrintTeam('sector')} key="sector">
                  Setor
                </Menu.Item>
              </Menu>
            }
            trigger={['click']}
          >
            <Button size="large">
              {t('Emitir via equipe')} <FiDownload />
            </Button>
          </Dropdown>
          <Button size="large" onClick={() => fetchPrintConsigneds()}>
            {t('Emitir consignados')} <FiDownload />
          </Button>
          <Can permission="@events/edit">
            <Button color="primary" size="large" title="Editar" onClick={() => handleModal()}>
              <FaPencilAlt style={{ marginRight: '10px' }} /> {t('Editar evento')}
            </Button>
          </Can>
          <FormEvents
            visible={formVisible}
            event={event}
            isMobile={isMobile}
            onClose={() => {
              setFormVisible(false);
              setTimeout(() => {
                onClose();
              }, 500);
            }}
          />
        </Flex>

        {/* INFORMAÇÕES BÁSICAS */}
        <TitleTag>{title('INFORMAÇÕES BÁSICAS')}</TitleTag>
        <Card>
          <TableView
            pagination={false}
            rowKey={() => 'basic-1'}
            bordered
            columns={[
              { title: label('Nome'), dataIndex: 'name', render: child },
              { title: label('Desconto'), dataIndex: 'price_discount', render: child },
              { title: label('Tipo de evento'), dataIndex: 'description', render: child },
              {
                title: label('Início'),
                dataIndex: 'starts_at',
                render: (value) => child(formatDateTime(value)),
              },
              {
                title: label('Fim'),
                dataIndex: 'ends_at',
                render: (value) => child(formatDateTime(value)),
              },
            ]}
            dataSource={event ? [event] : []}
          />
          <TableView
            pagination={false}
            rowKey={() => 'basic-2'}
            bordered
            columns={[
              { title: label('Cliente'), dataIndex: 'customer_name', render: child },
              { title: label('Vendedor'), dataIndex: 'seller_name', render: child },
              { title: label('Público'), dataIndex: 'estimated_people', render: child },
              { title: label('Status Evento'), dataIndex: 'status_text', render: child },
              { title: label('Status Pagamento'), dataIndex: 'payment_status_text', render: child },
            ]}
            dataSource={event ? [event] : []}
          />
          {/* ENDEREÇO */}
          <TitleTag>{title('Endereço')}</TitleTag>
          <TableView
            pagination={false}
            rowKey={() => 'basic-2'}
            bordered
            columns={[
              { title: label('CEP'), dataIndex: 'adress_cep', render: child },
              { title: label('UF'), dataIndex: 'state_uf', render: child },
              { title: label('Cidade'), dataIndex: ['city', 'name'], render: child },
              { title: label('Logradouro'), dataIndex: 'adress_street', render: child },
              { title: label('Número'), dataIndex: 'adress_number', render: child },
              { title: label('Referência'), dataIndex: 'adress_reference', render: child },
              { title: label('Nome do local'), dataIndex: 'adress_local_name', render: child },
            ]}
            dataSource={event ? [event] : []}
          />
        </Card>

        {/* Operacional / Setores */}
        <TitleTag>{title('Operacional/Setores')}</TitleTag>
        {event?.sectors?.map((sector, idx) => (
          <Card key={idx} title={title(sector.name)}>
            <TableView
              pagination={false}
              bordered
              size="small"
              rowKey={(record, index) => index}
              columns={[
                { title: label('Nome'), dataIndex: 'operational_name', width: '350px', render: child },
                { title: label('Quantidade'), dataIndex: 'amount', render: child },
                {
                  title: label('Valor unitário'),
                  dataIndex: 'price_unit',
                  render: (value) => child(formatPrice(value ?? 0, false)),
                },
                {
                  title: label('Valor total'),
                  dataIndex: 'price_total',
                  render: (value) => child(formatPrice(value ?? 0, false)),
                },
              ]}
              dataSource={sector?.sector_operationals}
              summary={() => {
                const totalSector = sector?.sector_operationals?.reduce(
                  (acc, so) => {
                    acc.priceTotal += parseFloat(so.price_total);
                    acc.amountTotal += so.amount;
                    return acc;
                  },
                  {
                    priceTotal: 0,
                    amountTotal: 0,
                  }
                );

                return (
                  <Table.Summary.Row style={{ backgroundColor: '#d6d6d6' }}>
                    <Table.Summary.Cell>{t('TOTAL')}</Table.Summary.Cell>
                    <Table.Summary.Cell>{totalSector.amountTotal}</Table.Summary.Cell>
                    <Table.Summary.Cell />
                    <Table.Summary.Cell>{formatPrice(totalSector.priceTotal, false)}</Table.Summary.Cell>
                  </Table.Summary.Row>
                );
              }}
            />
          </Card>
        ))}
        <TableView
          pagination={false}
          bordered
          dataSource={[{}]}
          columns={[
            {
              dataIndex: 'geralTotal',
              width: '1000px',
              render: () => child('TOTAL GERAL'),
            },
            {
              dataIndex: 'priceTotal',
              title: label('Valor total'),
              render: () => child(formatPrice(allSectorTotals?.priceTotal)),
            },
          ]}
          components={{
            body: {
              row: (props) => <tr {...props} style={{ backgroundColor: '#d6d6d6', fontWeight: 'bold' }} />,
            },
          }}
        />

        {/* SERVIÇOS */}
        <TitleTag>{title('Serviços')}</TitleTag>
        <TableView
          pagination={false}
          bordered
          size="small"
          rowKey={(record, index) => index}
          columns={[
            { title: label('Serviço'), dataIndex: ['service', 'name'], render: child },
            { title: label('Quantidade'), dataIndex: 'amount', render: child },
            {
              title: label('Valor unitário'),
              dataIndex: 'price_unit',
              render: (value) => child(formatPrice(value ?? 0, false)),
            },
            {
              title: label('Valor total'),
              dataIndex: 'price_total',
              render: (value) => child(formatPrice(value ?? 0, false)),
            },
          ]}
          dataSource={event?.services_events || []}
          summary={() => {
            const total = event?.services_events?.reduce(
              (acc, se) => {
                acc.price_total += parseFloat(se.price_total);

                return acc;
              },
              {
                price_total: 0,
              }
            );

            return (
              <Table.Summary.Row style={{ backgroundColor: '#d6d6d6' }}>
                <Table.Summary.Cell>{t('TOTAL SERVIÇOS')}</Table.Summary.Cell>
                <Table.Summary.Cell />
                <Table.Summary.Cell />
                <Table.Summary.Cell>{formatPrice(total.price_total)}</Table.Summary.Cell>
              </Table.Summary.Row>
            );
          }}
        />

        {/* DESPESAS */}
        <TitleTag>{title('Despesas')}</TitleTag>
        <TableView
          pagination={false}
          bordered
          size="small"
          rowKey={(record, index) => index}
          columns={[
            { title: label('Despesa'), dataIndex: ['expense', 'name'], render: child },
            { title: label('Quantidade'), dataIndex: 'amount', render: child },
            {
              title: label('Valor unitário'),
              dataIndex: 'price_unit',
              render: (value) => child(formatPrice(value ?? 0, false)),
            },
            {
              title: label('Valor total'),
              dataIndex: 'price_total',
              render: (value) => child(formatPrice(value ?? 0, false)),
            },
          ]}
          dataSource={event?.event_expenses || []}
          summary={() => {
            const total = event?.event_expenses?.reduce(
              (acc, ee) => {
                acc.price_total += parseFloat(ee.price_total);

                return acc;
              },
              {
                price_total: 0,
              }
            );

            return (
              <Table.Summary.Row style={{ backgroundColor: '#d6d6d6' }}>
                <Table.Summary.Cell>{t('TOTAL DESPESAS')}</Table.Summary.Cell>
                <Table.Summary.Cell />
                <Table.Summary.Cell />
                <Table.Summary.Cell>{formatPrice(total.price_total)}</Table.Summary.Cell>
              </Table.Summary.Row>
            );
          }}
        />

        {/* EQUIPE */}
        <TitleTag>{title('Equipe')}</TitleTag>
        {Object.values(groupedTeams).map((group, idx) => (
          <Card key={idx} title={group.sector_name} style={{ marginBottom: 16 }}>
            <TableView
              pagination={false}
              bordered
              size="small"
              rowKey={(record, index) => index}
              columns={[
                { title: label('Funcionário'), dataIndex: 'employee_name', render: child },
                { title: label('Setor'), dataIndex: 'sector_name', render: child },
                { title: label('Operacional'), dataIndex: 'operational_name', render: child },
                {
                  title: label('Paga'),
                  dataIndex: 'price_unit',
                  render: (value) => child(formatPrice(value ?? 0, false)),
                },
                { title: label('Saída'), dataIndex: 'departure_at', render: (value) => child(formatDateTime(value)) },
              ]}
              dataSource={group.records}
            />
          </Card>
        ))}

        {/* CONSIGNADOS */}
        <TitleTag>{title('Consignados')}</TitleTag>
        <TableView
          pagination={false}
          bordered
          size="small"
          rowKey={(record, index) => index}
          columns={[
            { title: label('Item'), dataIndex: 'item_name', render: child },
            { title: label('Quantidade'), dataIndex: 'amount', render: child },
            {
              title: label('Valor'),
              dataIndex: 'price_unit',
              render: (value) => child(formatPrice(value ?? 0, false)),
            },
            { title: label('Peso total'), dataIndex: 'weight_total', render: child },
            {
              title: label('Valor total'),
              dataIndex: 'price_total',
              render: (value) => child(formatPrice(value ?? 0, false)),
            },
          ]}
          dataSource={event?.consigneds || []}
          summary={() => {
            const total = event?.consigneds?.reduce(
              (acc, c) => {
                acc.weight_total += parseFloat(c.weight_total);
                acc.price_total += parseFloat(c.price_total);

                return acc;
              },
              {
                weight_total: 0,
                price_total: 0,
              }
            );

            return (
              <Table.Summary.Row style={{ backgroundColor: '#d6d6d6' }}>
                <Table.Summary.Cell>{t('TOTAL CONSIGNADOS')}</Table.Summary.Cell>
                <Table.Summary.Cell />
                <Table.Summary.Cell />
                <Table.Summary.Cell>{Number(total.weight_total).toFixed(3)}</Table.Summary.Cell>
                <Table.Summary.Cell>{formatPrice(total.price_total)}</Table.Summary.Cell>
              </Table.Summary.Row>
            );
          }}
        />

        {/* NOTAS E DESCRIÇÃO */}
        <TitleTag>{title('Notas e Descrição')}</TitleTag>
        <TableView
          pagination={false}
          bordered
          size="small"
          rowKey={() => 'notes'}
          columns={[
            { title: label('Nota Interna'), dataIndex: 'note_internal', render: renderHTML },
            { title: label('Nota Pública'), dataIndex: 'note_public', render: renderHTML },
            { title: label('Nota pós-evento'), dataIndex: 'note_after_event', render: renderHTML },
          ]}
          dataSource={event ? [event] : []}
        />
      </Spin>
    </Modal>
  );
}
